import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "../common/Icons";

// Real tournament photos from uploaded assets
const slides = [
  {
    title: "Campeonato Anual 2025",
    subtitle: "El torneo más grande del año",
    description: "Noticias, resultados y fotos de los eventos competitivos más recientes. Vive la emoción de cada duelo.",
    image: "https://customer-assets.emergentagent.com/job_card-nexus-5/artifacts/32zu9ujh_torneo%203v3%20-%202.jpg",
    cta: "Ver Resultados",
    ctaLink: "/ranking",
    accent: "#d4af37",
    tag: "EN VIVO",
  },
  {
    title: "Torneo 3v3",
    subtitle: "Forma tu equipo y compite",
    description: "Participa con tu equipo de duelistas y compite por premios increíbles en el formato por equipos.",
    image: "https://customer-assets.emergentagent.com/job_card-nexus-5/artifacts/wajgvn3q_Imagen%20de%20WhatsApp%202025-11-27%20a%20las%2022.15.40_05dc94f0.jpg",
    cta: "Inscribirse",
    ctaLink: "/torneos",
    accent: "#dc2626",
    tag: "TORNEO",
  },
  {
    title: "Comunidad Nirvana TCG",
    subtitle: "Más de 500 duelistas activos",
    description: "Únete a la comunidad más grande de duelistas de Venezuela. Comparte, aprende y compite.",
    image: "https://customer-assets.emergentagent.com/job_card-nexus-5/artifacts/ch1ehde2_Imagen%20de%20WhatsApp%202025-11-27%20a%20las%2020.01.07_de6d23b3.jpg",
    cta: "Ver Comunidad",
    ctaLink: "/comunidad",
    accent: "#7c3aed",
    tag: "COMUNIDAD",
  },
];

const HeroCarousel = () => {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState(1);
  const intervalRef = useRef(null);
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const particles = useRef([]);

  // Canvas particles
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener('resize', resize);
    particles.current = Array.from({ length: 80 }, () => ({
      x: Math.random() * (canvas.width || 1920),
      y: Math.random() * (canvas.height || 800),
      r: Math.random() * 1.5 + 0.3,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      alpha: Math.random() * 0.5 + 0.1,
      color: Math.random() > 0.6 ? '212,175,55' : Math.random() > 0.5 ? '220,38,38' : '168,85,247',
    }));
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.current.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color},${p.alpha})`;
        ctx.fill();
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
      });
      animFrameRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animFrameRef.current); window.removeEventListener('resize', resize); };
  }, []);

  const resetInterval = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => goTo('next'), 6000);
  };

  useEffect(() => {
    resetInterval();
    return () => clearInterval(intervalRef.current);
  }, []);

  const goTo = (dir) => {
    if (animating) return;
    setAnimating(true);
    setDirection(dir === 'next' ? 1 : -1);
    setTimeout(() => {
      setCurrent(prev => dir === 'next' ? (prev + 1) % slides.length : (prev - 1 + slides.length) % slides.length);
      setAnimating(false);
    }, 400);
    resetInterval();
  };

  const slide = slides[current];

  return (
    <section
      className="relative overflow-hidden"
      style={{ height: 'calc(100vh - 0px)', minHeight: '600px', maxHeight: '900px', background: '#050508' }}
      data-testid="hero-carousel"
    >
      {/* Background Image with parallax */}
      <div
        className="absolute inset-0 transition-opacity duration-500"
        style={{ opacity: animating ? 0 : 1 }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{
            backgroundImage: `url(${slide.image})`,
            filter: 'brightness(0.25) saturate(1.2)',
            transition: 'all 0.6s ease'
          }}
        />
        {/* Multi-layer gradient overlay */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(105deg, rgba(5,5,8,0.98) 0%, rgba(5,5,8,0.85) 40%, rgba(5,5,8,0.4) 70%, rgba(5,5,8,0.15) 100%)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(5,5,8,1) 0%, rgba(5,5,8,0.6) 25%, transparent 60%)' }} />
      </div>

      {/* Particle canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: 0.6 }} />

      {/* Accent line */}
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: `linear-gradient(to bottom, transparent, ${slide.accent}, transparent)`, transition: 'background 0.6s' }} />

      {/* Decorative grid pattern */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `linear-gradient(rgba(212,175,55,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.03) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} />

      {/* Right side: actual photo thumbnail */}
      <div
        className="absolute right-0 top-0 bottom-0 w-1/3 md:w-2/5 lg:w-1/2 pointer-events-none"
        style={{
          backgroundImage: `url(${slide.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: animating ? 0 : 1,
          transition: 'opacity 0.5s ease',
          maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0.9) 100%)',
          WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0.9) 100%)',
        }}
      />

      {/* Logo watermark */}
      <div className="absolute right-8 top-1/2 -translate-y-1/2 pointer-events-none opacity-5 hidden lg:block">
        <img src="/logo-nirvana.png" alt="" className="w-96 object-contain" style={{ filter: 'brightness(2)' }} />
      </div>

      {/* Content */}
      <div className="relative h-full flex items-center">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-xl" style={{ opacity: animating ? 0 : 1, transform: animating ? 'translateX(-20px)' : 'translateX(0)', transition: 'all 0.4s ease' }}>

            {/* Tag badge */}
            <div className="flex items-center gap-3 mb-6">
              <div
                className="flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest"
                style={{ background: `${slide.accent}20`, border: `1px solid ${slide.accent}60`, color: slide.accent }}
              >
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: slide.accent }} />
                {slide.tag}
              </div>
              <div className="h-px flex-1 max-w-24" style={{ background: `linear-gradient(to right, ${slide.accent}60, transparent)` }} />
            </div>

            {/* Title */}
            <h1 className="font-black mb-2 leading-none" style={{
              fontFamily: 'Cinzel, serif',
              fontSize: 'clamp(2.5rem, 6vw, 5rem)',
              background: `linear-gradient(135deg, #ffffff 0%, ${slide.accent} 60%, #ffffff 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: 'none'
            }}>
              {slide.title}
            </h1>

            {/* Subtitle */}
            <p className="font-bold mb-4" style={{ color: slide.accent, fontSize: '1.1rem', opacity: 0.9 }}>
              {slide.subtitle}
            </p>

            {/* Description */}
            <p className="text-gray-300 mb-8 leading-relaxed" style={{ fontSize: '1rem', maxWidth: '42ch' }}>
              {slide.description}
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4">
              <Link
                to={slide.ctaLink}
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-black text-sm uppercase tracking-wide transition-all hover:scale-105 hover:shadow-2xl"
                style={{
                  background: `linear-gradient(135deg, ${slide.accent}, ${slide.accent}bb)`,
                  color: slide.accent === '#d4af37' ? '#000' : '#fff',
                  boxShadow: `0 8px 30px ${slide.accent}40`,
                }}
              >
                {slide.cta}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link
                to="/marketplace"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-sm uppercase tracking-wide border transition-all hover:scale-105"
                style={{ border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.8)', background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(4px)' }}
              >
                Tienda
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom slide info + navigation */}
      <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between px-6 md:px-12 pb-8">
        {/* Slide counter + dots */}
        <div className="flex items-center gap-4">
          <span className="text-xs font-black" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Cinzel,serif' }}>
            0{current + 1} / 0{slides.length}
          </span>
          <div className="flex gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => { if (!animating) { setDirection(i > current ? 1 : -1); setCurrent(i); resetInterval(); } }}
                className="transition-all rounded-full"
                style={{
                  width: i === current ? '32px' : '8px',
                  height: '8px',
                  background: i === current ? slide.accent : 'rgba(255,255,255,0.2)',
                }}
              />
            ))}
          </div>
        </div>

        {/* Nav arrows */}
        <div className="flex gap-3">
          <button
            onClick={() => goTo('prev')}
            className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-110"
            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', color: 'rgba(255,255,255,0.7)' }}
          >
            <ChevronLeft />
          </button>
          <button
            onClick={() => goTo('next')}
            className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-110"
            style={{ background: `${slide.accent}20`, border: `1px solid ${slide.accent}60`, backdropFilter: 'blur(8px)', color: slide.accent }}
          >
            <ChevronRight />
          </button>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none" style={{ background: 'linear-gradient(to top, #050508, transparent)' }} />
    </section>
  );
};

export default HeroCarousel;
